import { Divider, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import { RightDrawer } from "lib/@components/drawer/right-drawer"
import { Repeat } from "lib/@components/repeat"
import { busyWhile } from "lib/@components/busy/busy-while"
import Iconify from "minimals-template/components/Iconify"
import { getScenarioDataQualityTasks } from "routes/scenarios/controller/scenario-data-quality-tasks"
import { getDataQualityCategoryLabel, getDataQualityDetail } from "./data-quality-detail-utils"
import { exportDataQualityTasksToExcel } from "./export-data-quality-tasks-to-excel"
import { DataQualityTaskRow } from "./data-quality-task-row"

export function DataQualityTasksDrawer({
    open,
    onClose,
    alertId,
    detail,
    regimeId,
    currencySymbol,
    scenarioId,
    variantId,
    regimeName,
}) {
    const shown = detail?.tasks?.length || 0
    const total = detail?.total || 0

    return (
        <RightDrawer open={open} setOpen={handleSetOpen} onClose={onClose}>
            <Stack data-cy={`data-quality-tasks-drawer-${alertId}`} spacing={2} sx={{ height: "100%" }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack>
                        <Typography variant="h6">{getDataQualityCategoryLabel(alertId)}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 14 }}>
                            {formatCount()}
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title="Export affected tasks to Excel">
                            <IconButton
                                size="small"
                                data-cy={`data-quality-tasks-export-${alertId}`}
                                aria-label="Export affected tasks to Excel"
                                onClick={handleExport}
                            >
                                <Iconify icon="mdi:microsoft-excel" />
                            </IconButton>
                        </Tooltip>
                        <IconButton
                            aria-label="Close"
                            size="small"
                            data-cy="data-quality-tasks-drawer-close"
                            onClick={onClose}
                        >
                            <Iconify icon="eva:close-fill" />
                        </IconButton>
                    </Stack>
                </Stack>

                <Divider sx={{ borderStyle: "dashed" }} />

                <Stack spacing={0} divider={<Divider sx={{ borderStyle: "dashed" }} />} sx={{ overflowY: "auto" }}>
                    <Repeat
                        list={detail?.tasks || []}
                        keyFn={getTaskRowKey}
                        item={
                            <DataQualityTaskRow alertId={alertId} regimeId={regimeId} currencySymbol={currencySymbol} />
                        }
                    />
                </Stack>
            </Stack>
        </RightDrawer>
    )

    function handleSetOpen(value) {
        if (!value) onClose?.()
    }

    function formatCount() {
        if (total > shown) return `Showing ${shown} of ${total} affected tasks`
        return `${total} affected ${total === 1 ? "task" : "tasks"}`
    }

    async function handleExport() {
        await busyWhile(exportTasks, "Exporting affected tasks...")
    }

    async function exportTasks() {
        const full = await getAllAffectedTasks()
        await exportDataQualityTasksToExcel({
            alertId,
            tasks: full.tasks,
            total: full.total,
            currencySymbol,
            regimeName,
        })
    }

    // The drawer only holds the capped rows persisted on the cost result; when the list
    // was capped, fetch the full set on demand (a server-side recompute, itself bounded
    // by DATA_QUALITY_EXPORT_CAP) so the export contains every affected task.
    async function getAllAffectedTasks() {
        const capped = total > shown
        if (!capped || !scenarioId) return { total, tasks: detail?.tasks || [] }
        const fullDetails = await getScenarioDataQualityTasks({ scenarioId, variantId: variantId || null })
        const full = getDataQualityDetail({ dataQualityDetails: fullDetails }, alertId)
        if (!full) return { total, tasks: detail?.tasks || [] }
        return { total: full.total, tasks: full.tasks }
    }
}

// Keyed on scheduleId (falls back to title) rather than scheduleCode, which can be empty
// and would collide across schedules.
function getTaskRowKey(task) {
    return `${task.scheduleId || task.scheduleTitle}:${task.taskId}`
}
